import React, { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Alert, Box, CircularProgress, Popover, Typography, useTheme } from '@mui/material';
import { fetchPatientReportsForHistory } from '../../../rtk-apis/reports/getMeasurementHistory';
import { CATALOG_BY_ID, isAbnormal, normalRangeFor, normalizePatientSex } from '../../reports/measurementCatalog';
import { normaliseUnit, parseReportMeasurementCell } from '../../../Utils/reportMeasurementLabels';
import {
  buildMeasurementHistoryPoints,
  formatMeasurementChange,
  formatMeasurementHistoryDate,
  HistoryReport,
  measurementChange,
} from '../../../Utils/measurementHistory';

interface HoverTarget {
  measurementId: string;
  /** Unit printed in the hovered cell; only values printed in the same unit are plotted. */
  unit: string;
  /** Where the card opens: just below the hovered cell (or above it when there is no
   * room below), so it never covers that cell. */
  top: number;
  left: number;
  above: boolean;
}

/** Card height used to decide whether it fits below the cell. */
const CARD_HEIGHT_PX = 480;

/** How long the pointer must rest on a value before the card opens. */
const OPEN_DELAY_MS = 700;

/**
 * The "Label: value" cell under the pointer. This is deliberately event delegation on
 * the Express panel: ReportLayout replaces its child DOM while it renders, but this
 * parent remains stable for the life of the open report. Each cell is read on its own —
 * a row holds four different measurements. `box` is the table cell when there is one,
 * so the label, the value and the cell padding all count as the same cell.
 */
function cellForTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return null;
  const cell = target.closest('td, li, p');
  if (!cell) return null;
  const box = target.closest('td, th') || cell;
  return { box, text: (cell.textContent || '').trim(), parsed: parseReportMeasurementCell(cell.textContent || '') };
}

interface Props {
  panel: HTMLElement | null;
  patientId?: string | null;
  sex?: string | null;
}

function MeasurementHistoryHoverInner({ panel, patientId, sex }: Props): React.ReactNode {
  const [hovered, setHovered] = useState<HoverTarget | null>(null);
  const [reports, setReports] = useState<HistoryReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reports load once per patient, on the first hover. Kept apart from `hovered` so moving
  // between cells never cancels the request in flight.
  const [historyWanted, setHistoryWanted] = useState(false);

  useEffect(() => {
    setHistoryWanted(false);
    setReports([]);
    setError(null);
  }, [patientId]);

  useEffect(() => {
    if (hovered) setHistoryWanted(true);
  }, [hovered]);

  useEffect(() => {
    if (!panel || !patientId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // The cell the pointer is resting on, and a cell the user clicked or typed in. The
    // card stays away from an edited cell until the pointer moves to another one.
    let currentBox: Element | null = null;
    let editingBox: Element | null = null;

    const cancelPending = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const onPointerOver = (event: PointerEvent) => {
      const cell = cellForTarget(event.target);
      const box = cell?.box || null;
      if (box === currentBox) return;
      currentBox = box;
      if (box !== editingBox) editingBox = null;
      cancelPending();
      setHovered(null);
      const parsed = cell?.parsed || null;
      if (!parsed || !box || editingBox) return;
      timer = setTimeout(() => {
        timer = null;
        if (currentBox !== box || editingBox === box || !box.isConnected) return;
        const rect = box.getBoundingClientRect();
        const above = rect.bottom + 6 + CARD_HEIGHT_PX > window.innerHeight && rect.top - 6 > CARD_HEIGHT_PX;
        setHovered({
          measurementId: parsed.measurementId,
          unit: parsed.unit,
          top: above ? rect.top - 6 : rect.bottom + 6,
          left: rect.left,
          above,
        });
      }, OPEN_DELAY_MS);
    };

    // A click or a key press means the user is working in the report: close the card and
    // keep it closed on this cell. Passive — the event itself is never touched.
    const onEdit = () => {
      editingBox = currentBox;
      cancelPending();
      setHovered(null);
    };

    const onMouseLeave = () => {
      currentBox = null;
      editingBox = null;
      cancelPending();
      setHovered(null);
    };

    // Capture phase so the panel sees events the editor may stop. Passive and read-only:
    // nothing is prevented, stopped or written to the editor's DOM, so the editor's own
    // handlers (field chaining, wording rules, smart fields, dropdowns) run exactly as before.
    panel.addEventListener('pointerover', onPointerOver, { capture: true, passive: true });
    panel.addEventListener('pointerdown', onEdit, { capture: true, passive: true });
    panel.addEventListener('keydown', onEdit, { capture: true, passive: true });
    panel.addEventListener('mouseleave', onMouseLeave, { passive: true });
    return () => {
      cancelPending();
      panel.removeEventListener('pointerover', onPointerOver, true);
      panel.removeEventListener('pointerdown', onEdit, true);
      panel.removeEventListener('keydown', onEdit, true);
      panel.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [panel, patientId]);

  useEffect(() => {
    if (!historyWanted || !patientId) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchPatientReportsForHistory(patientId).then((data: HistoryReport[]) => {
      if (!active) return;
      setReports(data);
      setLoading(false);
    }).catch((err: Error) => {
      if (!active) return;
      // The next hover tries again.
      setHistoryWanted(false);
      setError(err?.message || 'Could not load the patient’s reports');
      setLoading(false);
    });
    return () => { active = false; };
  }, [historyWanted, patientId]);

  const entry = hovered ? CATALOG_BY_ID[hovered.measurementId] : undefined;
  const points = useMemo(
    () => (hovered ? buildMeasurementHistoryPoints(reports, hovered.measurementId, hovered.unit) : []),
    [hovered, reports]
  );

  // The catalog range is in the catalog's unit; drawing it under values printed in
  // another unit would put the band in the wrong place.
  const unitMatchesCatalog = !!entry && normaliseUnit(entry.unit || '') === normaliseUnit(hovered?.unit || '');
  const patientSex = normalizePatientSex(sex);
  const normalRange = entry && unitMatchesCatalog ? normalRangeFor(entry.measurementId, patientSex) : undefined;
  const axisUnit = hovered?.unit || entry?.unit || '';
  const change = useMemo(() => measurementChange(points), [points]);
  // Out-of-range uses the same catalog range as the band, so it is skipped in another unit.
  const outOfRange = useMemo(
    () => points.map(point => !!entry && unitMatchesCatalog && isAbnormal(entry.measurementId, point.value, patientSex)),
    [entry, unitMatchesCatalog, points, patientSex]
  );
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const mutedColor = theme.palette.text.secondary;
  const hasBand = normalRange?.low !== undefined && normalRange?.high !== undefined;
  const options = useMemo<Highcharts.Options>(() => {
    const axisLabelStyle = { color: mutedColor, fontSize: '13px' };
    return {
      chart: { type: 'line', height: 280, backgroundColor: 'transparent', spacing: [16, 16, 8, 8], style: { fontFamily: 'inherit' } },
      title: { text: undefined }, credits: { enabled: false }, legend: { enabled: false },
      xAxis: {
        type: 'datetime',
        // One tick per study, so every dot has its date under it.
        tickPositions: points.length <= 8 ? points.map(point => point.timestamp) : undefined,
        minPadding: 0.06,
        maxPadding: 0.06,
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
        labels: { style: axisLabelStyle, formatter: function () { return Highcharts.dateFormat('%d/%m/%y', this.value as number); } },
      },
      yAxis: {
        title: { text: axisUnit || undefined, style: axisLabelStyle },
        labels: { style: axisLabelStyle },
        gridLineColor: theme.palette.divider,
        // Keep both normal limits in view, so it is clear where the values sit against them.
        softMin: hasBand ? normalRange?.low : undefined,
        softMax: hasBand ? normalRange?.high : undefined,
        plotBands: hasBand
          ? [{ from: normalRange?.low, to: normalRange?.high, color: 'rgba(76, 175, 80, 0.16)' }]
          : [],
      },
      tooltip: { style: { fontSize: '14px' }, formatter: function () {
        const point = this as Highcharts.Point & { custom?: { date: string; outOfRange: boolean } };
        const flag = point.custom?.outOfRange ? '<br/><span style="color:#d32f2f">Outside normal range</span>' : '';
        return `<b>${point.custom?.date || Highcharts.dateFormat('%d/%m/%Y', point.x as number)}</b><br/>${entry?.shortLabel}: ${point.y} ${axisUnit}${flag}`;
      } },
      series: [{
        type: 'line',
        name: entry?.label || '',
        color: '#26a69a',
        lineWidth: 3,
        marker: { enabled: true, radius: 6, lineWidth: 2, lineColor: '#26a69a', fillColor: '#26a69a' },
        dataLabels: { enabled: true, style: { color: textColor, fontSize: '14px', fontWeight: '500', textOutline: 'none' } },
        data: points.map((point, index) => ({
          x: point.timestamp,
          y: point.value,
          marker: outOfRange[index] ? { fillColor: '#ef5350', lineColor: '#ef5350', radius: 7 } : undefined,
          dataLabels: outOfRange[index] ? { style: { color: '#ef5350' } } : undefined,
          custom: { date: formatMeasurementHistoryDate(point.date), outOfRange: outOfRange[index] },
        })),
      }],
    };
  }, [axisUnit, entry?.label, entry?.shortLabel, hasBand, mutedColor, normalRange?.high, normalRange?.low, outOfRange, points, textColor, theme.palette.divider]);

  const latest = points[points.length - 1];
  const decimals = change?.decimals ?? 1;
  const rangeText = hasBand ? `${normalRange?.low}–${normalRange?.high} ${axisUnit}` : null;

  const open = Boolean(hovered && entry);

  return (
    <Popover
      open={open}
      onClose={() => setHovered(null)}
      anchorReference="anchorPosition"
      anchorPosition={hovered ? { top: hovered.top, left: hovered.left } : undefined}
      transformOrigin={{ vertical: hovered?.above ? 'bottom' : 'top', horizontal: 'left' }}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      disableScrollLock
      // A hover card must not take the pointer: Popover's full-page backdrop would
      // otherwise sit under the mouse, fire the panel's mouseleave and close it again.
      sx={{ pointerEvents: 'none' }}
      PaperProps={{ sx: { width: 600, maxWidth: 'calc(100vw - 32px)', p: 2.5 } }}
    >
      <Typography fontWeight={500} sx={{ fontSize: '1.15rem', lineHeight: 1.3 }}>{entry?.label}</Typography>
      <Typography variant="body2" color="text.secondary">From this patient’s saved reports</Typography>
      <Box sx={{ mt: 1.5 }}>
        {loading ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={28} /></Box> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && !error && points.length < 2 ? (
          <Alert severity="info">
            {points.length === 1
              ? 'Only one saved report has this value. History needs at least two.'
              : 'No saved report has this value.'}
          </Alert>
        ) : null}
        {!loading && !error && change && latest ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Latest ({formatMeasurementHistoryDate(latest.date)})</Typography>
              <Typography fontWeight={500} sx={{ fontSize: '1.4rem', lineHeight: 1.3, color: outOfRange[outOfRange.length - 1] ? '#ef5350' : 'text.primary' }}>
                {latest.value} {axisUnit}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Change since {formatMeasurementHistoryDate(change.firstDate)}</Typography>
              <Typography fontWeight={500} sx={{ fontSize: '1.4rem', lineHeight: 1.3 }}>
                {formatMeasurementChange(change.sinceFirst, decimals)} {axisUnit}
              </Typography>
              {change.perYear !== null ? (
                <Typography variant="body1" color="text.secondary">
                  {formatMeasurementChange(change.perYear, decimals)} {axisUnit ? `${axisUnit} per year` : 'per year'}
                </Typography>
              ) : null}
            </Box>
            {points.length > 2 ? (
              <Box>
                <Typography variant="body2" color="text.secondary">Since previous ({formatMeasurementHistoryDate(change.previousDate)})</Typography>
                <Typography fontWeight={500} sx={{ fontSize: '1.4rem', lineHeight: 1.3 }}>
                  {formatMeasurementChange(change.sincePrevious, decimals)} {axisUnit}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
        {!loading && !error && points.length >= 2 ? <HighchartsReact highcharts={Highcharts} options={options} /> : null}
        {!loading && !error && points.length >= 2 && (rangeText || outOfRange.some(Boolean)) ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
            {rangeText ? (
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: 'rgba(76, 175, 80, 0.35)' }} />
                Normal: {rangeText}
              </Typography>
            ) : null}
            {outOfRange.some(Boolean) ? (
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef5350' }}>
                <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef5350' }} />
                Outside normal range
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Popover>
  );
}

/**
 * Keeps the hover card isolated: any failure inside it renders nothing instead of
 * unmounting the Express page around it.
 */
class MeasurementHistoryBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error('[MeasurementHistory] hover card failed; disabled for this report', error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export default function MeasurementHistoryHover(props: Props): React.ReactNode {
  return (
    <MeasurementHistoryBoundary>
      <MeasurementHistoryHoverInner {...props} />
    </MeasurementHistoryBoundary>
  );
}
